// Demo data store: seeds IndexedDB on first run and exposes async accessors
// used by the demo router. resetDemo() wipes + reseeds with NO page reload.

import {
  STORES,
  idbGetAll,
  idbGet,
  idbPut,
  idbBulkPut,
  idbDelete,
  idbClearAll,
} from "./idb";
import { buildSeed } from "./seed";
import { resetCounters } from "./limits";

const SEED_FLAG_ID = "seeded_v3";

let readyPromise = null;

async function isSeeded() {
  const flag = await idbGet(STORES.meta, SEED_FLAG_ID);
  return Boolean(flag && flag.value);
}

async function seedAll() {
  const seed = buildSeed();
  await idbBulkPut(STORES.customers, seed.customers);
  await idbBulkPut(STORES.suppliers, seed.suppliers);
  await idbBulkPut(STORES.karigars, seed.karigars);
  await idbBulkPut(STORES.inventory, seed.inventory);
  await idbBulkPut(STORES.estimates, seed.estimates);
  await idbBulkPut(STORES.repairs, seed.repairs);
  await idbBulkPut(STORES.orders, seed.orders);
  await idbBulkPut(STORES.purchases, seed.purchases);
  await idbBulkPut(STORES.purchaseReturns, seed.purchaseReturns);
  await idbBulkPut(STORES.consignmentMemos, seed.consignmentMemos);
  await idbBulkPut(STORES.payments, seed.payments);
  await idbBulkPut(STORES.expenses, seed.expenses);
  await idbBulkPut(STORES.bulkBuckets, seed.bulkBuckets);
  await idbBulkPut(STORES.bulkLedger, seed.bulkLedger);
  await idbBulkPut(STORES.pipelineLeads, seed.pipelineLeads);
  await idbBulkPut(STORES.goldRates, seed.goldRates);
  await idbBulkPut(STORES.goldRateHistory, seed.goldRateHistory);
  await idbBulkPut(STORES.dashboardTasks, seed.dashboardTasks);
  await idbPut(STORES.analyticsCache, { id: "analytics", ...seed.analytics });
  await idbPut(STORES.meta, { id: SEED_FLAG_ID, value: true, at: new Date().toISOString() });
}

// Idempotent: ensures the demo DB is seeded before any read/write.
export function ensureReady() {
  if (!readyPromise) {
    readyPromise = (async () => {
      if (!(await isSeeded())) {
        await seedAll();
      }
    })().catch((err) => {
      // Reset the gate so a later call can retry (e.g. transient IDB open error).
      readyPromise = null;
      throw err;
    });
  }
  return readyPromise;
}

// Reset: clear everything, reseed, clear session counters. No reload — callers
// dispatch the `jf-demo-reset` event so the SPA refetches.
export async function resetDemo() {
  readyPromise = null;
  await idbClearAll();
  await seedAll();
  resetCounters();
  readyPromise = Promise.resolve();
}

// ---- generic list helpers ----
export async function list(storeName) {
  await ensureReady();
  return idbGetAll(storeName);
}
export async function getById(storeName, id) {
  await ensureReady();
  return idbGet(storeName, id);
}
export async function put(storeName, value) {
  await ensureReady();
  return idbPut(storeName, value);
}
export async function remove(storeName, id) {
  await ensureReady();
  return idbDelete(storeName, id);
}

// ---- analytics ----
export async function getAnalytics() {
  await ensureReady();
  return (await idbGet(STORES.analyticsCache, "analytics")) || {};
}

// ---- AI history (append-only) ----
export async function appendAiHistory(entry) {
  await ensureReady();
  return idbPut(STORES.aiHistory, { ...entry, at: new Date().toISOString() });
}

export { STORES };
